import { Components } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { getTagChipVariants } from './tag';

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    dismissible: true;
    selectable: true;
  }
}

type ChipVisualState = {
  backgroundColor: string;
  border: string;
  color: string;
  iconColor?: string;
};

type ChipStateStyles = {
  default: ChipVisualState;
  hover: ChipVisualState;
};

const getChipFocusOutline = (theme: Theme, inverted = false) => ({
  outline: `${theme.lelySpacing._2XS} solid ${
    inverted ? theme.palette.text.light : theme.palette.border.focus
  }`,
  outlineOffset: theme.lelySpacing._2XS,
});

const getDismissibleChipStates = (theme: Theme): ChipStateStyles => ({
  default: {
    backgroundColor: theme.palette.background.secondaryDefault,
    border: `1px solid ${theme.palette.border.default}`,
    color: theme.palette.text.default,
    iconColor: theme.palette.text.subtle,
  },
  hover: {
    backgroundColor: theme.palette.background.secondaryHover,
    border: `1px solid ${theme.palette.border.default}`,
    color: theme.palette.text.default,
    iconColor: theme.palette.text.subtle,
  },
});

const getDismissibleInvertedChipStates = (theme: Theme): ChipStateStyles => ({
  default: {
    backgroundColor: theme.palette.background.secondaryInvertedDefault,
    border: `1px solid ${theme.palette.border.light}`,
    color: theme.palette.text.light,
    iconColor: theme.palette.text.light,
  },
  hover: {
    backgroundColor: theme.palette.background.secondaryInvertedHover,
    border: `1px solid ${theme.palette.border.light}`,
    color: theme.palette.text.light,
    iconColor: theme.palette.text.light,
  },
});

export const getSelectableUnselectedChipStates = (
  theme: Theme,
): ChipStateStyles => ({
  default: {
    backgroundColor: theme.palette.background.secondaryDefault,
    border: `1px solid ${theme.palette.border.default}`,
    color: theme.palette.text.default,
  },
  hover: {
    backgroundColor: theme.palette.background.secondaryHover,
    border: `1px solid ${theme.palette.border.default}`,
    color: theme.palette.text.default,
  },
});

const getSelectableSelectedChipStates = (theme: Theme): ChipStateStyles => ({
  default: {
    backgroundColor: theme.palette.background.primaryDefault,
    border: '1px solid transparent',
    color: theme.palette.text.light,
  },
  hover: {
    backgroundColor: theme.palette.background.primaryHover,
    border: '1px solid transparent',
    color: theme.palette.text.light,
  },
});

const getSelectableInvertedUnselectedChipStates = (
  theme: Theme,
): ChipStateStyles => ({
  default: {
    backgroundColor: theme.palette.background.secondaryInvertedDefault,
    border: `1px solid ${theme.palette.border.light}`,
    color: theme.palette.text.light,
  },
  hover: {
    backgroundColor: theme.palette.background.secondaryInvertedHover,
    border: `1px solid ${theme.palette.border.light}`,
    color: theme.palette.text.light,
  },
});

const getSelectableInvertedSelectedChipStates = (
  theme: Theme,
): ChipStateStyles => ({
  default: {
    backgroundColor: theme.palette.background.primaryInvertedDefault,
    border: '1px solid transparent',
    color: theme.palette.text.light,
  },
  hover: {
    backgroundColor: theme.palette.background.primaryInvertedHover,
    border: '1px solid transparent',
    color: theme.palette.text.light,
  },
});

const getIconColorStyles = (iconColor?: string) =>
  iconColor
    ? {
        '& .MuiChip-deleteIcon, & .icon-svg': {
          color: iconColor,
        },
      }
    : {};

const applyChipVisualState = (state: ChipVisualState) => ({
  backgroundColor: state.backgroundColor,
  border: state.border,
  color: state.color,
  ...getIconColorStyles(state.iconColor),
});

const getFocusStyles = (
  state: ChipVisualState,
  theme: Theme,
  inverted: boolean,
) => ({
  ...applyChipVisualState(state),
  ...getChipFocusOutline(theme, inverted),
});

const getChipVariantStateStyles = (
  theme: Theme,
  states: ChipStateStyles,
  inverted: boolean,
  selectedStates?: ChipStateStyles,
) => ({
  ...applyChipVisualState(states.default),

  '&:hover': applyChipVisualState(states.hover),

  '&.Mui-focusVisible, &:focus-within': getFocusStyles(
    states.default,
    theme,
    inverted,
  ),

  '&.Mui-disabled': {
    ...applyChipVisualState(states.default),
    opacity: 0.32,

    '& .MuiChip-deleteIcon, & .icon-svg': {
      flexShrink: 0,
    },
  },

  ...(selectedStates
    ? {
        '&[data-selected="true"]': {
          ...applyChipVisualState(selectedStates.default),

          '&:hover': applyChipVisualState(selectedStates.hover),

          '&.Mui-focusVisible, &:focus-within': getFocusStyles(
            selectedStates.default,
            theme,
            inverted,
          ),

          '&.Mui-disabled': {
            ...applyChipVisualState(selectedStates.default),
            opacity: 0.32,
          },
        },
      }
    : {}),
});

const getChipStateStyles = (
  theme: Theme,
  defaultStates: ChipStateStyles,
  invertedStates: ChipStateStyles,
  selectedStates?: ChipStateStyles,
  invertedSelectedStates?: ChipStateStyles,
) => ({
  ...getChipVariantStateStyles(theme, defaultStates, false, selectedStates),

  '&[data-inverted="true"]': getChipVariantStateStyles(
    theme,
    invertedStates,
    true,
    invertedSelectedStates,
  ),
});

const getChipTypographyStyles = (theme: Theme) => ({
  ...theme.typography.base,
  fontSize: theme.lelyFontSize.SM,
  lineHeight: theme.lelyLineHeight.BASE,
});

const getDenseChipLayoutStyles = (theme: Theme) => ({
  height: theme.lelySpacing.XL,
  minHeight: 'unset',
});

const getDismissibleDefaultIconStyles = (theme: Theme) => ({
  '& .MuiChip-deleteIcon': {
    margin: 0,
    width: theme.lelySpacing._2XL,
    height: theme.lelySpacing._2XL,
    flexShrink: 0,
  },

  '& .icon-svg': {
    width: theme.lelySpacing._2XL,
    height: theme.lelySpacing._2XL,
    flexShrink: 0,
  },
});

const getDismissibleDenseStyles = (theme: Theme) => ({
  ...getDenseChipLayoutStyles(theme),
  padding: `0 ${theme.lelySpacing._2XS} 0 ${theme.lelySpacing.XS}`,

  '& .MuiChip-deleteIcon': {
    width: theme.lelySpacing.XL,
    height: theme.lelySpacing.XL,
    flexShrink: 0,
  },

  '& .icon-svg': {
    width: theme.lelySpacing.XL,
    height: theme.lelySpacing.XL,
    flexShrink: 0,
  },
});

const getSelectableDenseStyles = (theme: Theme) => ({
  ...getDenseChipLayoutStyles(theme),
  padding: `0 ${theme.lelySpacing.XS}`,
});

export const getChipComponents = (
  theme: Theme,
): Pick<Components, 'MuiChip'> => {
  const dismissibleStates = getDismissibleChipStates(theme);
  const dismissibleInvertedStates = getDismissibleInvertedChipStates(theme);
  const selectableUnselectedStates = getSelectableUnselectedChipStates(theme);
  const selectableSelectedStates = getSelectableSelectedChipStates(theme);
  const selectableInvertedUnselectedStates =
    getSelectableInvertedUnselectedChipStates(theme);
  const selectableInvertedSelectedStates =
    getSelectableInvertedSelectedChipStates(theme);

  return {
    MuiChip: {
      styleOverrides: {
        root: {
          ...getChipTypographyStyles(theme),
          borderRadius: theme.lelyBorderRadius.SM,
          gap: theme.lelySpacing.XS,
          cursor: 'pointer',

          '& .MuiChip-label': {
            padding: 0,
          },
        },
      },

      variants: [
        {
          props: { variant: 'dismissible' },
          style: {
            padding: `${theme.lelySpacing.XS} ${theme.lelySpacing.XS} ${theme.lelySpacing.XS} ${theme.lelySpacing.SM}`,
            ...getDismissibleDefaultIconStyles(theme),

            '&[data-dense="true"]': getDismissibleDenseStyles(theme),

            ...getChipStateStyles(
              theme,
              dismissibleStates,
              dismissibleInvertedStates,
            ),
          },
        },
        {
          props: { variant: 'selectable' },
          style: {
            padding: `${theme.lelySpacing.XS} ${theme.lelySpacing.SM}`,

            '&[data-dense="true"]': getSelectableDenseStyles(theme),

            ...getChipStateStyles(
              theme,
              selectableUnselectedStates,
              selectableInvertedUnselectedStates,
              selectableSelectedStates,
              selectableInvertedSelectedStates,
            ),
          },
        },
        ...(getTagChipVariants(theme) ?? []),
      ],
    },
  };
};
